import React, { useState, useEffect } from "react";
import {
  TrendingUp,
  Search,
  Clock,
  Users,
  BarChart3,
  Calendar,
  RefreshCw,
  Trash2,
} from "../utils/icons";
import {
  getSearchAnalytics,
  getPopularSearches,
  getSearchHistory,
  clearSearchHistory,
} from "../services/enhancedSearchService";

interface SearchAnalyticsDashboardProps {
  className?: string;
}

function SearchAnalyticsDashboard({
  className = "",
}: SearchAnalyticsDashboardProps) {
  const [analytics, setAnalytics] = useState<any[]>([]);
  const [popularSearches, setPopularSearches] = useState<
    Array<{ query: string; count: number }>
  >([]);
  const [searchHistory, setSearchHistory] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<
    "overview" | "popular" | "history" | "performance"
  >("overview");
  const [timeRange, setTimeRange] = useState<"7d" | "30d" | "90d" | "all">(
    "30d",
  );

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = () => {
    const allAnalytics = getSearchAnalytics();
    const allPopularSearches = getPopularSearches();
    const allSearchHistory = getSearchHistory();

    // Filter by time range
    const now = new Date();
    const filteredAnalytics = allAnalytics.filter((a) => {
      const searchDate = new Date(a.timestamp);
      const daysDiff =
        (now.getTime() - searchDate.getTime()) / (1000 * 60 * 60 * 24);

      switch (timeRange) {
        case "7d":
          return daysDiff <= 7;
        case "30d":
          return daysDiff <= 30;
        case "90d":
          return daysDiff <= 90;
        case "all":
          return true;
        default:
          return true;
      }
    });

    setAnalytics(filteredAnalytics);
    setPopularSearches(allPopularSearches);
    setSearchHistory(allSearchHistory);
  };

  const getOverviewStats = () => {
    if (analytics.length === 0)
      return { totalSearches: 0, avgResults: 0, avgTime: 0, uniqueQueries: 0 };

    const totalSearches = analytics.length;
    const avgResults =
      analytics.reduce((sum, a) => sum + a.resultsCount, 0) / totalSearches;
    const avgTime =
      analytics.reduce((sum, a) => sum + a.searchTime, 0) / totalSearches;
    const uniqueQueries = new Set(analytics.map((a) => a.query)).size;

    return { totalSearches, avgResults, avgTime, uniqueQueries };
  };

  const getPerformanceMetrics = () => {
    if (analytics.length === 0) return [];

    // Group by day
    const dailyStats: {
      [key: string]: { searches: number; avgTime: number; avgResults: number };
    } = {};

    analytics.forEach((a) => {
      const date = new Date(a.timestamp).toDateString();
      if (!dailyStats[date]) {
        dailyStats[date] = { searches: 0, avgTime: 0, avgResults: 0 };
      }
      dailyStats[date].searches++;
      dailyStats[date].avgTime += a.searchTime;
      dailyStats[date].avgResults += a.resultsCount;
    });

    // Calculate averages
    Object.keys(dailyStats).forEach((date) => {
      const stats = dailyStats[date];
      stats.avgTime = stats.avgTime / stats.searches;
      stats.avgResults = stats.avgResults / stats.searches;
    });

    return Object.entries(dailyStats)
      .map(([date, stats]) => ({ date, ...stats }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const handleClearHistory = () => {
    if (
      window.confirm(
        "Are you sure you want to clear all search history? This action cannot be undone.",
      )
    ) {
      clearSearchHistory();
      loadAnalytics();
    }
  };

  const stats = getOverviewStats();
  const performanceData = getPerformanceMetrics();

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Search Analytics
          </h2>
          <div className="flex items-center space-x-4">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as any)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#F98B3D]"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
            <button
              onClick={loadAnalytics}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              title="Refresh data"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          {[
            { key: "overview", label: "Overview", icon: BarChart3 },
            { key: "popular", label: "Popular Searches", icon: TrendingUp },
            { key: "history", label: "Search History", icon: Clock },
            { key: "performance", label: "Performance", icon: Search },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === key
                  ? "border-[#F98B3D] text-[#F98B3D]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Icon className="h-4 w-4 mr-2" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Search className="h-8 w-8 text-blue-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-blue-600">
                      Total Searches
                    </p>
                    <p className="text-2xl font-bold text-blue-900">
                      {stats.totalSearches}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Users className="h-8 w-8 text-green-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-600">
                      Unique Queries
                    </p>
                    <p className="text-2xl font-bold text-green-900">
                      {stats.uniqueQueries}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Clock className="h-8 w-8 text-yellow-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-yellow-600">
                      Avg Search Time
                    </p>
                    <p className="text-2xl font-bold text-yellow-900">
                      {stats.avgTime.toFixed(0)}ms
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <TrendingUp className="h-8 w-8 text-purple-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-purple-600">
                      Avg Results
                    </p>
                    <p className="text-2xl font-bold text-purple-900">
                      {stats.avgResults.toFixed(1)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Recent Search Activity
              </h3>
              <div className="space-y-2">
                {analytics.slice(0, 10).map((search, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {search.query}
                      </p>
                      <p className="text-sm text-gray-500">
                        {search.resultsCount} results •{" "}
                        {search.searchTime.toFixed(0)}ms
                      </p>
                    </div>
                    <span className="text-xs text-gray-400">
                      {new Date(search.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "popular" && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Most Popular Searches
            </h3>
            <div className="space-y-3">
              {popularSearches.length > 0 ? (
                popularSearches.map((search, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center">
                      <span className="text-lg font-bold text-gray-400 mr-3">
                        #{index + 1}
                      </span>
                      <span className="font-medium text-gray-900">
                        {search.query}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {search.count} searches
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">
                  No search data available yet
                </p>
              )}
            </div>
          </div>
        )}

        {activeTab === "history" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Search History
              </h3>
              <button
                onClick={handleClearHistory}
                className="flex items-center px-3 py-1 text-sm text-red-600 hover:text-red-800 transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Clear History
              </button>
            </div>
            <div className="space-y-2">
              {searchHistory.length > 0 ? (
                searchHistory.map((search, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {search.query}
                      </p>
                      <p className="text-sm text-gray-500">
                        {search.resultCount} results •{" "}
                        {search.clickedResults.length} clicks
                      </p>
                    </div>
                    <span className="text-xs text-gray-400">
                      {new Date(search.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">
                  No search history available
                </p>
              )}
            </div>
          </div>
        )}

        {activeTab === "performance" && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Search Performance Over Time
            </h3>
            <div className="space-y-4">
              {performanceData.length > 0 ? (
                performanceData.map((day, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">
                        {new Date(day.date).toLocaleDateString()}
                      </span>
                      <span className="text-sm text-gray-500">
                        {day.searches} searches
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Avg Time:</span>
                        <span className="ml-2 font-medium">
                          {day.avgTime.toFixed(0)}ms
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Avg Results:</span>
                        <span className="ml-2 font-medium">
                          {day.avgResults.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">
                  No performance data available
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchAnalyticsDashboard;
